import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MagnifyingGlassIcon, UserPlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Input, SearchableSelect, Button } from '@/components/common';
import { useCustomers, useCreateCustomer } from '@/hooks';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { Customer } from '@/types';
import { cn } from '@/utils';
import { normalizeStoredPincode, type MotorQuoteFormData } from '../../schema';

type Mode = 'select' | 'create';

/**
 * Customer step (Wave F1 item 4b) — the first "real" motor step, right after
 * Insurance Type. Lets the broker employee pick WHO the quote is for: search
 * and select an existing customer, or add a new one inline without leaving
 * the wizard. Previously there was no such step at all — a fresh
 * /quotes/new silently used the logged-in employee's own identity as the
 * proposer (see item-04-diagnosis.md, 4b).
 *
 * Selecting/creating a customer snapshots its identity fields onto the motor
 * form itself (customer_name/phone/email/dob/gender) rather than keeping a
 * separate lookup — QuoteWizardPage's onMotorSubmit and ReviewStep read
 * those fields directly, so the submitted proposer always matches what's
 * shown here even if the underlying customer record changes later.
 */
interface CustomerStepProps {
  /** B-02/C1. Ask for (and gate Next on) the proposer's PIN. MOTOR ONLY: it is
   *  what the insurer zone-rates on and what the GoDigit payload builder
   *  refuses without. The generic branch reuses this step verbatim for types
   *  that are not zone-rated and whose schema does not declare the field, so
   *  rendering it there would put a required-looking input in front of a
   *  broker that nothing validates. */
  requirePincode?: boolean;
}

export function CustomerStep({ requirePincode = false }: CustomerStepProps = {}) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<MotorQuoteFormData>();

  const [mode, setMode] = useState<Mode>('select');
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // SearchableSelect owns its own search box state internally (it's
  // uncontrolled here) and only reports it via onQueryChange -- so there's
  // no `term` to render, just `setQuery` to receive keystrokes and
  // `debouncedQuery` (300ms) to actually feed the customer lookup. Mirrors
  // the pattern PolicyCreatePage already uses for its customer search.
  const { debouncedTerm: debouncedQuery, setTerm: setQuery } = useDebouncedSearch();
  const [newCustomer, setNewCustomer] = useState({ first_name: '', last_name: '', phone: '', email: '' });

  const customerId = watch('customer_id');
  const customerName = watch('customer_name');
  const customerPhone = watch('customer_phone');
  const customerPincode = watch('customer_pincode');

  // Queue #227: this dropdown showed the first 20 customers and nothing else.
  // The same cap was fixed on the Add Policy customer picker (BROKER-2a) and
  // never carried over here -- so a broker with 300 contacts could reach 20 of
  // them from the New Quote flow. Same remedy: a debounced SERVER-side search
  // (already wired below via onQueryChange) plus a wider slice, so the
  // unsearched list is 100 deep and anything past that is one keystroke away.
  // 100 is the server's max_page_size, not an arbitrary choice.
  const { data: customersPage, isLoading } = useCustomers(1, {
    search: debouncedQuery || undefined,
    page_size: 100,
  });
  const customers = useMemo(() => customersPage?.results ?? [], [customersPage]);

  // Keep the chosen customer in the option list even after a later search
  // narrows the results past them -- otherwise picking someone and then typing
  // a new query blanks the control's label and it reads as if the selection
  // were lost. (PolicyCreatePage pins the same way.)
  const [pinnedCustomer, setPinnedCustomer] = useState<Customer | null>(null);

  const options = useMemo(() => {
    const list = customers.map((c) => ({
      value: c.id,
      label: c.phone ? `${c.full_name} — ${c.phone}` : c.full_name,
    }));
    if (pinnedCustomer && !list.some((o) => o.value === pinnedCustomer.id)) {
      list.unshift({
        value: pinnedCustomer.id,
        label: pinnedCustomer.phone
          ? `${pinnedCustomer.full_name} — ${pinnedCustomer.phone}`
          : pinnedCustomer.full_name,
      });
    }
    return list;
  }, [customers, pinnedCustomer]);

  const createCustomer = useCreateCustomer();

  function applyCustomer(c: Customer) {
    setPinnedCustomer(c);
    setValue('customer_id', c.id, { shouldValidate: true, shouldDirty: true });
    setValue('customer_name', c.full_name, { shouldValidate: false });
    setValue('customer_phone', c.phone || '', { shouldValidate: false });
    setValue('customer_email', c.email || '', { shouldValidate: false });
    setValue('customer_dob', c.date_of_birth || '', { shouldValidate: false });
    setValue('customer_gender', c.gender || '', { shouldValidate: false });
    // B-02/C1. Validated, unlike the rest of the snapshot: this one gates Next,
    // because the GoDigit payload builder refuses a motor quote without it.
    // Blank for the 59-in-408 customers whose record has none (and for every
    // customer added through the branch below, which does not ask), which is
    // exactly when the broker must be asked rather than left to find out at
    // the end.
    if (requirePincode) {
      setValue('customer_pincode', normalizeStoredPincode(c.pincode), { shouldValidate: true });
    }
  }

  function handleSelectExisting(id: string) {
    const c = customers.find((x) => x.id === id);
    if (c) applyCustomer(c);
  }

  function handleCreateCustomer() {
    if (!newCustomer.first_name.trim() || !newCustomer.phone.trim()) return;
    createCustomer.mutate(
      {
        customer_type: 'individual',
        first_name: newCustomer.first_name.trim(),
        last_name: newCustomer.last_name.trim() || undefined,
        phone: newCustomer.phone.trim(),
        email: newCustomer.email.trim() || undefined,
      },
      {
        onSuccess: (created) => {
          applyCustomer(created);
          setMode('select');
          setNewCustomer({ first_name: '', last_name: '', phone: '', email: '' });
        },
      }
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customer</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Who is this quote for? Select an existing customer or add a new one.
      </p>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => setMode('select')}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
            mode === 'select'
              ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          )}
        >
          <MagnifyingGlassIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Select Existing Customer
        </button>
        <button
          type="button"
          onClick={() => setMode('create')}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
            mode === 'create'
              ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          )}
        >
          <UserPlusIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Add New Customer
        </button>
      </div>

      {mode === 'select' && (
        <div className="sm:max-w-md space-y-3">
          <SearchableSelect
            id="quote-customer"
            label="Customer"
            options={options}
            value={customerId || ''}
            onChange={handleSelectExisting}
            onQueryChange={setQuery}
            placeholder={isLoading ? 'Loading...' : 'Type to search all customers by name or phone'}
            error={errors.customer_id?.message}
            required
          />
        </div>
      )}

      {mode === 'create' && (
        <div className="sm:max-w-md space-y-4">
          <Input
            label="First Name"
            value={newCustomer.first_name}
            onChange={(e) => setNewCustomer((s) => ({ ...s, first_name: e.target.value }))}
            required
          />
          <Input
            label="Last Name"
            value={newCustomer.last_name}
            onChange={(e) => setNewCustomer((s) => ({ ...s, last_name: e.target.value }))}
          />
          <Input
            label="Phone"
            value={newCustomer.phone}
            onChange={(e) => setNewCustomer((s) => ({ ...s, phone: e.target.value }))}
            required
          />
          <Input
            label="Email"
            type="email"
            value={newCustomer.email}
            onChange={(e) => setNewCustomer((s) => ({ ...s, email: e.target.value }))}
          />
          <Button
            type="button"
            onClick={handleCreateCustomer}
            isLoading={createCustomer.isPending}
            disabled={!newCustomer.first_name.trim() || !newCustomer.phone.trim()}
          >
            Add Customer
          </Button>
        </div>
      )}

      {/* B-02/C1. Only once a customer is chosen -- asking for a PIN before
          there is anyone to attach it to reads as a stray field. Prefilled
          from their record above; the broker only types when it is empty or
          wrong. */}
      {requirePincode && customerId && (
        <div className="mt-6 sm:max-w-md">
          <Input
            id="quote-customer-pincode"
            name="customer_pincode"
            label="PIN code"
            required
            inputMode="numeric"
            maxLength={6}
            helperText="Motor premiums are zone-rated on this, so the insurer will not quote without it."
            error={errors.customer_pincode?.message}
            value={customerPincode || ''}
            onChange={(e) =>
              setValue('customer_pincode', e.target.value.replace(/\D/g, '').slice(0, 6), {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </div>
      )}

      {customerId && customerName && (
        <div className="mt-6 flex items-center gap-2 p-4 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-700 text-sm text-green-800 dark:text-green-300">
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>
            Selected: <span className="font-medium">{customerName}</span>
            {customerPhone ? ` — ${customerPhone}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
